import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import SEO from "@/components/SEO";
import { faqLd } from "@/lib/structured-data";

const FAQS = [
  {
    question: "What is VIZ?",
    answer:
      "VIZ is an AI-powered mental performance training app for athletes. It generates personalized guided visualizations — competition prep, practice rehearsal, and recovery — using the PETTLEP sports-science framework and delivers them as premium narrated audio sessions tailored to your sport, experience level, and goals.",
  },
  {
    question: "Does sports visualization actually improve athletic performance?",
    answer:
      "Yes. A 2025 multilevel meta-analysis of 86 studies (Liu et al., Behavioral Sciences) found that imagery practice produces a moderate-to-large positive effect on athletic performance. The PETTLEP imagery model (Holmes & Collins, 2001) — which VIZ is built on — has been validated across 23+ peer-reviewed papers and is used by sports psychologists with elite athletes worldwide.",
  },
  {
    question: "What is the PETTLEP imagery model?",
    answer:
      "PETTLEP is a sports-psychology framework introduced by Holmes & Collins (2001) that stands for Physical, Environment, Task, Timing, Learning, Emotion, and Perspective. It's based on functional equivalence — the idea that imagined movement activates many of the same neural pathways as real movement. VIZ scripts are explicitly structured against the PETTLEP elements so each session primes the brain like real sport-specific rehearsal.",
  },
  {
    question: "How is VIZ different from Calm or Headspace?",
    answer:
      "Calm and Headspace are general mindfulness apps. VIZ is built specifically for athletes: every script is sport-specific, uses competition language, and follows the PETTLEP framework rather than generic meditation patterns. Sessions include breathwork, place-setting, multi-sensory rehearsal of your sport, and confidence cues tied to your actual goals — not relaxation for its own sake.",
  },
  {
    question: "How long is a VIZ session?",
    answer:
      "Most sessions run 6–12 minutes — long enough to enter a focused state and complete a full PETTLEP-structured visualization, short enough to fit before practice, after warm-up, or in your pre-bed routine.",
  },
  {
    question: "What sports does VIZ support?",
    answer:
      "VIZ supports all major competitive sports including soccer, basketball, golf, tennis, swimming, track & field, volleyball, baseball, MMA, climbing, cycling, skiing, hockey, and more. The questionnaire adapts to your sport so the script references your specific environment, equipment, and skill demands.",
  },
  {
    question: "Can visualization help with sports anxiety and pre-game nerves?",
    answer:
      "Yes — it's one of the best-supported uses of imagery. Studies on competitive state anxiety show that structured imagery interventions can reduce cognitive anxiety and shift somatic arousal toward a facilitative interpretation. VIZ's recovery and competition modes both include calming breathwork and confidence rehearsal designed to break the pre-game anxiety spiral.",
  },
  {
    question: "When should I use VIZ — before competition, during practice, or for recovery?",
    answer:
      "All three. Use Competition mode in the 24 hours before an event to rehearse execution and build calm confidence. Use Practice mode to add mental reps to skill work and accelerate learning. Use Recovery mode after setbacks, injuries, or rough sessions to process emotion, reset focus, and protect long-term motivation.",
  },
  {
    question: "How much does VIZ cost?",
    answer:
      "VIZ starts at $15/month for the Essential plan (1 visualization per week) or $25/month for Elite (3 per week with priority generation). Both plans include a 7-day free trial, all sport types, all voices, and progress tracking. Annual pricing is available at a discount.",
  },
  {
    question: "Is there a free trial?",
    answer:
      "Yes. Every new account gets a 7-day free trial of any plan. No commitment, cancel anytime from your profile.",
  },
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" className="py-24 md:py-32 relative">
      <SEO
        title="VIZ — AI-Powered Mental Training for Athletes"
        description="Personalized guided visualizations for competition, practice, and recovery."
        path="/"
        jsonLd={faqLd(FAQS)}
      />

      <div className="container px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <motion.h2
            className="font-display text-3xl md:text-5xl font-bold mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Frequently asked <span className="text-gradient-primary">questions</span>
          </motion.h2>
          <p className="text-muted-foreground text-lg">
            Everything you wanted to know about visualization training.
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-3">
          {FAQS.map((faq, i) => {
            const open = openIndex === i;
            return (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: Math.min(i, 6) * 0.04 }}
                className="rounded-xl border border-border/50 gradient-card overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(open ? null : i)}
                  className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left hover:bg-secondary/20 transition-colors"
                  aria-expanded={open}
                >
                  <span className="font-display text-base md:text-lg font-semibold">
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform duration-300 ${
                      open ? "rotate-180 text-primary" : ""
                    }`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 md:px-6 pb-6 text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
